/**
 * @file     dataPresentation.ts
 * @readme   ./README.md
 * @purpose  Central, deterministic display rules for public numeric data.
 *           Exact formats preserve comparisons; compact formats are reserved
 *           for summary cards where the underlying value is available nearby.
 */

const cadExact = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const count = new Intl.NumberFormat('en-CA', {
  maximumFractionDigits: 0,
});

export function formatCadExact(
  value: number | null | undefined,
  unavailable = 'Unavailable',
): string {
  return value == null || !Number.isFinite(value)
    ? unavailable
    : cadExact.format(Math.round(value));
}

/**
 * Summary-only currency format. Never use it to compare adjacent values or
 * render a time series because close values can collapse to the same label.
 */
export function formatCadCompact(
  value: number | null | undefined,
  unavailable = 'Unavailable',
): string {
  if (value == null || !Number.isFinite(value)) return unavailable;
  const rounded = Math.round(value);
  // 999,500 rounds to 1000K at thousands precision, so it belongs to millions.
  if (Math.abs(rounded) >= 999_500) {
    return `$${(rounded / 1_000_000).toFixed(2).replace(/\.00$/, '')}M`;
  }
  if (Math.abs(rounded) >= 1_000) return `$${Math.round(rounded / 1_000)}K`;
  return cadExact.format(rounded);
}

export function formatDataCount(value: number): string {
  return count.format(Math.round(value));
}

export function formatPercent(
  ratio: number | null | undefined,
  fractionDigits = 1,
  unavailable = 'Unavailable',
): string {
  if (ratio == null || !Number.isFinite(ratio)) return unavailable;
  return `${(ratio * 100).toFixed(fractionDigits)}%`;
}
